import React, { useEffect, useMemo, useState } from "react";
import {
  PenLine,
  FileText,
  User,
  AlertCircle,
  Calendar,
  ChevronRight,
  Clock3,
  ShieldCheck,
} from "lucide-react";
import { getPendingSigningRequests } from "../../../api/hrm/signing/signingRequests";
import { formatDateVN } from "../../../utils/formatUtils";
import {
  PENDING_STATUS_LABEL,
  DOCUMENT_TYPE_LABEL,
  URGENCY_LABEL,
  STATUS_BADGE,
} from "./signingConstants";

const PENDING_STATUSES = new Set([
  "pending_sign_coo",
  "pending_approve_coo",
  "pending_sign_ceo",
]);

const STATUS_ACCENT = {
  pending_sign_coo: "from-amber-500 to-orange-500",
  pending_approve_coo: "from-amber-400 to-amber-600",
  pending_sign_ceo: "from-orange-500 to-red-600",
};

const matchesPendingFilters = (item, filters = {}) => {
  const {
    keyword = "",
    status = "",
    document_type = "",
    signer_role = "",
    urgency = "",
    issued_from = "",
    issued_to = "",
  } = filters;

  // Lọc trạng thái ngoài nhóm chờ → ẩn toàn bộ thẻ chờ
  if (status && status !== "pending" && !PENDING_STATUSES.has(status)) {
    return false;
  }
  if (status && status !== "pending" && item.status !== status) {
    return false;
  }

  if (document_type && item.document_type !== document_type) return false;
  if (signer_role && item.signer_role !== signer_role) return false;
  if (urgency && item.urgency !== urgency) return false;

  if (issued_from && item.issued_date) {
    if (String(item.issued_date).slice(0, 10) < issued_from) return false;
  } else if (issued_from && !item.issued_date) {
    return false;
  }

  if (issued_to && item.issued_date) {
    if (String(item.issued_date).slice(0, 10) > issued_to) return false;
  } else if (issued_to && !item.issued_date) {
    return false;
  }

  const kw = keyword.trim().toLowerCase();
  if (kw) {
    const haystack = [
      item.title,
      item.request_code,
      item.document_number,
      item.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(kw)) return false;
  }

  return true;
};

const PendingSigningList = ({ onSelect, refreshKey, filters }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getPendingSigningRequests();
        setItems(res.records || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey]);

  const filtered = useMemo(
    () => items.filter((item) => matchesPendingFilters(item, filters)),
    [items, filters]
  );

  if (loading) {
    return (
      <p className="text-xs text-gray-400 italic p-4 text-center">
        Đang tải hàng chờ...
      </p>
    );
  }

  if (!filtered.length) return null;

  return (
    <div className="mb-6" data-tour="hrm-signing-pending">
      <div className="mb-4 flex items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 text-white shadow-md shadow-red-100 flex items-center justify-center shrink-0">
            <Clock3 size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="font-black text-base uppercase tracking-tight text-gray-800">
              Yêu cầu chờ xử lý
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              Văn bản cần bạn duyệt hoặc ký trong luồng trình ký
            </p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center justify-center min-w-[2rem] h-8 px-2.5 rounded-full bg-red-50 border border-red-100 text-red-700 text-xs font-black tabular-nums">
          {filtered.length}
          {filtered.length !== items.length ? (
            <span className="text-red-400 font-semibold ml-0.5">/{items.length}</span>
          ) : null}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((item) => {
          const urgent = item.urgency && item.urgency !== "normal";
          const accent =
            STATUS_ACCENT[item.status] || "from-red-500 to-red-700";

          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect?.(item)}
              className={`relative overflow-hidden text-left rounded-2xl border bg-white transition-all duration-200 group
                hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-100/80 hover:border-red-200
                focus:outline-none focus-visible:ring-4 focus-visible:ring-red-500/15
                ${urgent ? "border-red-200 shadow-sm shadow-red-50" : "border-gray-100 shadow-sm"}`}
            >
              <div
                className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`}
              />

              <div className="p-4 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-bold ${
                      STATUS_BADGE[item.status] ||
                      "bg-amber-50 text-amber-800 border-amber-200"
                    }`}
                  >
                    {PENDING_STATUS_LABEL[item.status] || item.status}
                  </span>
                  {urgent ? (
                    <span className="text-[10px] px-2 py-1 rounded-full border border-red-200 bg-red-50 text-red-700 font-bold inline-flex items-center gap-1 shrink-0">
                      <AlertCircle size={11} />
                      {URGENCY_LABEL[item.urgency] || item.urgency}
                    </span>
                  ) : null}
                </div>

                <div className="mt-3 flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-50 to-white border border-red-100 text-red-600 flex items-center justify-center shrink-0 group-hover:from-red-100 group-hover:border-red-200 transition-colors">
                    <FileText size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
                      {item.request_code}
                    </p>
                    <p className="mt-1 text-sm font-bold text-gray-900 line-clamp-2 leading-snug group-hover:text-red-700 transition-colors">
                      {item.title}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {item.document_type ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-gray-600 px-2 py-1 bg-gray-50 border border-gray-100 rounded-lg">
                      {DOCUMENT_TYPE_LABEL[item.document_type] || item.document_type}
                    </span>
                  ) : null}
                  {item.signer_role ? (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-red-700 px-2 py-1 bg-red-50 border border-red-100 rounded-lg inline-flex items-center gap-1">
                      <ShieldCheck size={11} />
                      {item.signer_role}
                    </span>
                  ) : null}
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-gray-50/80 border border-gray-100 px-2.5 py-2 min-w-0">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                      Người tạo
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-gray-700 truncate inline-flex items-center gap-1 max-w-full">
                      <User size={12} className="text-red-500 shrink-0" />
                      <span className="truncate">{item.created_by_name || "HCNS"}</span>
                    </p>
                  </div>
                  <div className="rounded-xl bg-gray-50/80 border border-gray-100 px-2.5 py-2 min-w-0">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                      Ban hành
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-gray-700 truncate inline-flex items-center gap-1">
                      <Calendar size={12} className="text-red-500 shrink-0" />
                      {formatDateVN(item.issued_date || item.created_at)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                  <span className="text-[10px] text-gray-400 font-medium">
                    Tạo {formatDateVN(item.created_at)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-red-100 group-hover:shadow-md group-hover:shadow-red-200 transition-all">
                    <PenLine size={12} />
                    Xử lý
                    <ChevronRight
                      size={13}
                      className="opacity-80 group-hover:translate-x-0.5 transition-transform"
                    />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PendingSigningList;
